/**
 * Origem da visita (UTMs) guardada em cookie para ser gravada na igreja no
 * cadastro.
 *
 * O relatório da plataforma de anúncios já diz QUANTOS cadastros uma campanha
 * gerou. Ele não diz qual igreja pagante veio de qual palavra-chave — e é isso
 * que decide onde colocar o orçamento no mês seguinte (ver
 * marketing/google-ads-campanha.md). Por isso a origem viaja da primeira visita
 * até o `createChurch`.
 *
 * Guardamos os dois toques:
 *
 * - `first` nunca é sobrescrito. É o que trouxe a pessoa para o produto.
 * - `last` é atualizado a cada visita que chega com origem nova. É o que
 *   fechou.
 *
 * A distinção importa aqui porque o Instagram orgânico do Ecclesiato é forte:
 * só com first-touch, um anúncio que converteu alguém que já conhecia a marca
 * ficaria invisível; só com last-touch, o orgânico levaria a culpa de nunca
 * vender.
 */

const ATTRIBUTION_COOKIE_NAME = "eccl_attr";
const ATTRIBUTION_COOKIE_DAYS = 30;

/** Teto por valor. O cookie vem da URL, que é entrada de terceiro. */
const MAX_VALUE_LENGTH = 200;

/**
 * De onde a pessoa veio, num toque só.
 *
 * Os nomes seguem os parâmetros de URL do plano de campanha, sem o prefixo
 * `utm_`, mais os dois identificadores de clique que as plataformas anexam
 * sozinhas.
 */
export interface AcquisitionTouch {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  id?: string;
  /** `plc` — Feed, Reels, Stories. */
  placement?: string;
  /** `rede` — ig, fb, an, msg. */
  network?: string;
  /** `fbclid` (Meta) ou `gclid` (Google). */
  clickId?: string;
  /** Caminho da página em que a pessoa caiu, sem a query. */
  landingPage?: string;
  /** Host de onde ela veio, quando o navegador informa. */
  referrer?: string;
  /** ISO 8601. */
  at?: string;
}

export interface ChurchAcquisition {
  first?: AcquisitionTouch;
  last?: AcquisitionTouch;
}

/** Parâmetro na URL → campo do toque. */
const PARAM_MAP: Record<string, keyof AcquisitionTouch> = {
  utm_source: "source",
  utm_medium: "medium",
  utm_campaign: "campaign",
  utm_term: "term",
  utm_content: "content",
  utm_id: "id",
  plc: "placement",
  rede: "network",
};

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function clean(value: string | null): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, MAX_VALUE_LENGTH);
}

/**
 * Monta o toque a partir da URL atual. Devolve `null` quando a visita não
 * carrega origem nenhuma — caso em que não há o que gravar e o cookie que já
 * existe fica intacto.
 */
function readTouchFromUrl(): AcquisitionTouch | null {
  const params = new URLSearchParams(window.location.search);
  const touch: AcquisitionTouch = {};

  for (const [param, field] of Object.entries(PARAM_MAP)) {
    const value = clean(params.get(param));
    if (value) touch[field] = value;
  }

  const clickId = clean(params.get("fbclid")) ?? clean(params.get("gclid"));
  if (clickId) touch.clickId = clickId;

  const referrer = readReferrerHost();
  const aiSource = touch.source
    ? aiSourceOf(touch.source)
    : referrer && !aiReferrerRead
      ? aiSourceOf(referrer)
      : null;

  if (aiSource) {
    // O ChatGPT põe só `utm_source=chatgpt.com`; Perplexity, Gemini, Claude e
    // Copilot não põem nada e só se reconhecem pelo referrer. Os dois casos
    // viram o mesmo toque, `{fonte} / ai`, para o painel separar a IA do
    // tráfego direto.
    touch.source = aiSource;
    touch.medium ??= "ai";
    aiReferrerRead = true;
  }

  // Sem nenhum parâmetro de campanha, sem id de clique e sem vir de uma IA,
  // isso é tráfego orgânico ou direto: não sobrescreve nada.
  if (Object.keys(touch).length === 0) return null;

  touch.landingPage = window.location.pathname.slice(0, MAX_VALUE_LENGTH);
  touch.at = new Date().toISOString();
  if (referrer) touch.referrer = referrer;

  return touch;
}

/**
 * Assistentes de IA que mandam visita, pelo domínio. A chave é o que o
 * referrer (ou o `utm_source`) traz; o valor é o nome gravado em `source`.
 */
const AI_SOURCES: Record<string, string> = {
  "chatgpt.com": "chatgpt.com",
  "chat.openai.com": "chatgpt.com",
  "perplexity.ai": "perplexity.ai",
  "gemini.google.com": "gemini.google.com",
  "bard.google.com": "gemini.google.com",
  "copilot.microsoft.com": "copilot.microsoft.com",
  "claude.ai": "claude.ai",
  "chat.deepseek.com": "deepseek.com",
  "grok.com": "grok.com",
  "meta.ai": "meta.ai",
  "chat.mistral.ai": "mistral.ai",
};

function aiSourceOf(hostOrSource: string): string | null {
  const host = hostOrSource.toLowerCase().replace(/^www\./, "");
  return AI_SOURCES[host] ?? null;
}

/**
 * O referrer não muda na navegação interna da SPA: sem esta trava, cada troca
 * de `?search` regravaria o toque da IA com um `at` novo.
 */
let aiReferrerRead = false;

function readReferrerHost(): string | undefined {
  if (!document.referrer) return undefined;
  try {
    const host = new URL(document.referrer).host;
    return host === window.location.host ? undefined : host;
  } catch {
    // Referrer malformado não é motivo para perder o resto do toque.
    return undefined;
  }
}

/**
 * Lê os parâmetros de origem da URL atual e grava no cookie. Chamada em toda
 * navegação (ver `ReferralCapture`), porque a pessoa pode entrar pelo anúncio
 * numa página e se cadastrar dias depois em outra.
 */
export function captureAttributionCookie() {
  const touch = readTouchFromUrl();
  if (!touch) return;

  const stored = getAcquisition();
  const acquisition: ChurchAcquisition = {
    first: stored?.first ?? touch,
    last: touch,
  };

  setCookie(
    ATTRIBUTION_COOKIE_NAME,
    JSON.stringify(acquisition),
    ATTRIBUTION_COOKIE_DAYS,
  );
}

/**
 * A origem guardada, ou `null`. O retorno vai direto para o documento da
 * igreja, então nunca devolve `undefined`: o Firestore recusa o campo.
 */
export function getAcquisition(): ChurchAcquisition | null {
  const raw = getCookie(ATTRIBUTION_COOKIE_NAME);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    // Cookie é editável pelo visitante; só aceita o que tem a forma esperada.
    if (!parsed || typeof parsed !== "object") return null;
    const acquisition: ChurchAcquisition = {};
    if (parsed.first && typeof parsed.first === "object") {
      acquisition.first = parsed.first;
    }
    if (parsed.last && typeof parsed.last === "object") {
      acquisition.last = parsed.last;
    }
    return acquisition.first || acquisition.last ? acquisition : null;
  } catch {
    return null;
  }
}

/**
 * Resume a origem numa linha, para o painel admin e para log.
 * Ex.: `meta / paid_social · VENDAS_BR_ECC_LEAD_SITE_2026-09 · AD_RLS_...`
 */
export function formatTouch(touch?: AcquisitionTouch): string {
  if (!touch) return "—";
  const origin = [touch.source, touch.medium].filter(Boolean).join(" / ");
  return (
    [origin, touch.campaign, touch.content].filter(Boolean).join(" · ") ||
    "direto"
  );
}
